import { DEFAULT_LOCALE, type SupportedLanguage } from './config';
import { I18N_BACKEND_CONFIG } from './constants';

import LocalStorageBackend from 'i18next-localstorage-backend';
import HttpBackend from 'i18next-http-backend';
import { API_ROUTES } from '@/lib/consts/api';
import { httpClient } from '@/lib/http-client';
import { serverLogger } from '../services/serverLogger';
import { LoggerEventNames } from '../consts/logger';
import { LocalizationState } from '@/hooks/useLocalizationState';

interface FetchLanguagesResult {
    supportedLanguages: string[];
    languagesList: SupportedLanguage[];
}

/**
 * Collapses API rows that share the same display language (e.g. Spanish/ES + Spanish/MX).
 * Mirrors store v1 i18n fetchSupportedLanguages dedupe by `language` name.
 */
export function deduplicateLanguagesByName(languages: SupportedLanguage[]): SupportedLanguage[] {
    const seenLanguageNames = new Set<string>();

    return languages.filter((item) => {
        if (seenLanguageNames.has(item.language)) {
            return false;
        }

        seenLanguageNames.add(item.language);
        return true;
    });
}

export async function fetchSupportedLanguages(
    publisherId: string,
    localizationState: LocalizationState
): Promise<FetchLanguagesResult> {
    try {
        const response = await httpClient.get<SupportedLanguage[]>(
            API_ROUTES.LANGUAGE_SUPPORTED(publisherId)
        );

        if (!response.data || !Array.isArray(response.data)) {
            return { supportedLanguages: [DEFAULT_LOCALE], languagesList: [] };
        }

        const languagesList = deduplicateLanguagesByName(response.data);
        localizationState.setLanguagesList(languagesList);

        const nonDefaultLanguages = languagesList.filter(
            (item) => item.isoCode !== DEFAULT_LOCALE
        );
        const supportedLanguages = [
            DEFAULT_LOCALE,
            ...nonDefaultLanguages.map((item) => item.isoCode),
        ];

        return { supportedLanguages, languagesList };
    } catch (error) {
        serverLogger.error(LoggerEventNames.LANGUAGE_ERROR, {
            error: error,
        });
        return { supportedLanguages: [DEFAULT_LOCALE], languagesList: [] };
    }
}

export function getInitialLanguage(
    providedLanguage?: string,
    savedLanguage?: string | null
): string {
    return providedLanguage || savedLanguage || DEFAULT_LOCALE;
}

export function setLanguageCookie(language: string, displayName?: string): void {
    if (typeof document !== 'undefined') {
        const maxAge = 365 * 24 * 60 * 60;
        document.cookie = `NEXT_LOCALE=${language}; path=/; max-age=${maxAge}; SameSite=Lax`;
        if (displayName) {
            document.cookie = `NEXT_LOCALE_NAME=${encodeURIComponent(displayName)}; path=/; max-age=${maxAge}; SameSite=Lax`;
        }
    }
}

export function createBackendConfig(publisherId: string, baseURL: string, languagesList: SupportedLanguage[]) {
    return {
        backends: [LocalStorageBackend, HttpBackend],
        backendOptions: [
            {
                prefix: I18N_BACKEND_CONFIG.LOCAL_STORAGE_PREFIX,
                expirationTime: I18N_BACKEND_CONFIG.EXPIRATION_TIME_MS,
            },
            {
                loadPath: (lngs: string[]): string => {
                    const lng = lngs[0];
                    const localeData = languagesList.find((lang) => lang.isoCode === lng);
                    const languageName = localeData?.language || lng;
                    return `${baseURL}/store/translations/${publisherId}/languages/${languageName}`;
                },
            },
        ],
        cacheHitMode: 'refreshAndUpdateStore',
    };
}

